import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  IconButton,
  Grid,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  Calendar as CalendarIcon,
  FileText,
  Search,
  TrendingUp,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import LoaderProgress from '../components/ui/loader-progress';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StatWidget = ({ title, value, subtitle, icon: Icon, color }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: 'rgba(145, 158, 171, 0.24)',
      boxShadow:
        '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
    }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Paper>
);

export default function PasspPrimaNota() {
  const today = new Date();
  const initialDateFrom = `${today.getFullYear()}-01-01`;
  const initialDateTo = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(today.getDate()).padStart(2, '0')}`;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_registr');
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [azienda, setAzienda] = useState('all');
  const [dateFrom, setDateFrom] = useState(initialDateFrom);
  const [dateTo, setDateTo] = useState(initialDateTo);
  const [aziendeOptions, setAziendeOptions] = useState([]);
  const [cauOptions, setCauOptions] = useState([]);
  const [selectedCausali, setSelectedCausali] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({
    progressivo: false,
  });
  const activeRequestIdRef = useRef(0);
  const activeStatsRequestIdRef = useRef(0);
  const [summary, setSummary] = useState({
    total: 0,
    totalDare: 0,
    totalAvere: 0,
    saldo: 0,
  });

  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncAzienda, setSyncAzienda] = useState('');
  const [syncMode, setSyncMode] = useState('incremental');
  const [syncRebuild, setSyncRebuild] = useState(false);
  const [syncDateFrom, setSyncDateFrom] = useState('');
  const [syncDateTo, setSyncDateTo] = useState('');
  const [syncLatestDate, setSyncLatestDate] = useState(null);
  const [loadingLatestDate, setLoadingLatestDate] = useState(false);
  const { addJob } = useJob();

  const totalColumns = columnVisibility.progressivo ? 11 : 10;

  const formatCurrency = (value) => {
    if (value == null) return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return num.toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    });
  };

  const formatDisplayDate = (value) => {
    if (!value || typeof value !== 'string') return value;
    const parts = value.split('-');
    if (parts.length !== 3) return value;
    const [yyyy, mm, dd] = parts;
    return `${dd}/${mm}/${yyyy}`;
  };

  const getTodayYmd = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
      today.getDate()
    ).padStart(2, '0')}`;
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleFilterAzienda = (event) => {
    setAzienda(event.target.value);
    setPage(0);
  };

  const handleFilterDateFrom = (event) => {
    setDateFrom(event.target.value);
    setPage(0);
  };

  const handleFilterDateTo = (event) => {
    setDateTo(event.target.value);
    setPage(0);
  };

  const handleFilterCausali = (event) => {
    const value = event.target.value;
    setSelectedCausali(typeof value === 'string' ? value.split(',') : value);
    setPage(0);
  };

  const handleToggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const handleOpenColumnMenu = (event) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleCloseColumnMenu = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSync = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const firstDay = '01';

    setSyncMode('incremental');
    setSyncRebuild(false);
    setSyncLatestDate(null);

    setSyncDateFrom(`${year}-${month}-${firstDay}`);
    setSyncDateTo(`${year}-${month}-${day}`);

    if (!syncAzienda && aziendeOptions.length === 1) {
      setSyncAzienda(aziendeOptions[0].code);
    }

    setSyncDialogOpen(true);
  };

  const handleConfirmSync = async () => {
    if (!syncAzienda) {
      toast.error('Seleziona l’azienda per la sincronizzazione');
      return;
    }
    if (syncMode === 'range' && (!syncDateFrom || !syncDateTo)) {
      toast.error('Compila data inizio e data fine per la sincronizzazione a intervallo');
      return;
    }

    setSyncing(true);
    const pendingToastId = toast.loading('Avvio sincronizzazione Prima Nota Passpartout...');
    try {
      const incrementalFrom = syncLatestDate || getTodayYmd();
      const incrementalTo = getTodayYmd();
      const res = await api.post('/passp-prima-nota/sync', {
        azienda: syncAzienda,
        mode: syncMode,
        date_from: syncMode === 'range' ? syncDateFrom : incrementalFrom,
        date_to: syncMode === 'range' ? syncDateTo : incrementalTo,
        rebuild: syncMode === 'range' ? syncRebuild : false,
      });
      const jobId = res.data?.jobId;
      const startMessage = res.data?.message || 'Sincronizzazione Prima Nota Passpartout avviata';
      if (jobId) {
        addJob(jobId, 'passp_prima_nota_sync', { silent: true });
        toast.update(pendingToastId, {
          render: startMessage,
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        });
      } else {
        toast.update(pendingToastId, {
          render: startMessage,
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        });
      }
      setSyncDialogOpen(false);
    } catch (error) {
      console.error('Prima Nota sync error:', error);
      toast.update(pendingToastId, {
        render: 'Errore durante la sincronizzazione Prima Nota Passpartout',
        type: 'error',
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const fetchLatestDate = async () => {
      if (!syncDialogOpen || !syncAzienda || syncMode !== 'incremental') {
        return;
      }
      setLoadingLatestDate(true);
      try {
        const res = await api.get('/passp-prima-nota/sync/latest-date', {
          params: { azienda: syncAzienda },
        });
        setSyncLatestDate(res.data?.latestDate || null);
      } catch (error) {
        setSyncLatestDate(null);
        toast.error('Errore nel recupero ultima data registrazione');
      } finally {
        setLoadingLatestDate(false);
      }
    };
    fetchLatestDate();
  }, [syncDialogOpen, syncAzienda, syncMode]);

  useEffect(() => {
    if (syncMode === 'incremental' && syncRebuild) {
      setSyncRebuild(false);
    }
  }, [syncMode, syncRebuild]);

  const fetchFilters = async () => {
    try {
      const res = await api.get('/passp-prima-nota/filters/options');
      const data = res.data || {};
      setAziendeOptions((data.aziende || []).filter(Boolean));
      setCauOptions((data.causali || []).filter(Boolean));
    } catch (error) {
      console.error('Error fetching Prima Nota filters:', error);
      toast.error('Errore nel caricamento dei filtri Prima Nota Passpartout');
    }
  };

  const fetchData = async () => {
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection: order,
      };
      if (searchValue) params.search = searchValue;
      if (azienda !== 'all') params.azienda = azienda;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (selectedCausali.length > 0) {
        params.cauContabile = selectedCausali.join(',');
      }

      const res = await api.get('/passp-prima-nota', { params });
      if (activeRequestIdRef.current !== requestId) {
        return;
      }
      const data = res.data || {};
      setRows(Array.isArray(data.data) ? data.data : []);
      setTotalItems(data.total || 0);
    } catch (error) {
      console.error('Error fetching Prima Nota rows:', error);
      if (error.response) {
        toast.error('Errore nel caricamento Prima Nota Passpartout');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const requestId = activeStatsRequestIdRef.current + 1;
    activeStatsRequestIdRef.current = requestId;
    try {
      const params = {};
      if (searchValue) params.search = searchValue;
      if (azienda !== 'all') params.azienda = azienda;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (selectedCausali.length > 0) {
        params.cauContabile = selectedCausali.join(',');
      }

      const res = await api.get('/passp-prima-nota/stats', { params });
      if (activeStatsRequestIdRef.current !== requestId) {
        return;
      }
      const data = res.data || {};
      setSummary({
        total: data.total || 0,
        totalDare: data.totalDare || 0,
        totalAvere: data.totalAvere || 0,
        saldo: data.saldo || 0,
      });
    } catch (error) {
      console.error('Error fetching Prima Nota stats:', error);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, order, orderBy, searchValue, azienda, dateFrom, dateTo, selectedCausali]);

  useEffect(() => {
    fetchStats();
  }, [searchValue, azienda, dateFrom, dateTo, selectedCausali]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const handler = setTimeout(() => {
      setPage(0);
      if (trimmed.length >= 3) {
        setSearchValue(trimmed);
      } else {
        setSearchValue('');
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const empty = !loading && rows.length === 0;

  const saldoLabel = useMemo(() => formatCurrency(summary.saldo), [summary]);
  const activeFilters = useMemo(() => {
    const items = [];
    if (searchValue) {
      items.push({ key: 'search', label: `Ricerca: ${searchValue}` });
    }
    if (azienda !== 'all') {
      const found = aziendeOptions.find((item) => item.code === azienda);
      items.push({ key: 'azienda', label: `Azienda: ${found?.name || azienda}` });
    }
    if (selectedCausali.length > 0) {
      if (selectedCausali.length === 1) {
        items.push({ key: 'causali', label: `Causale: ${selectedCausali[0]}` });
      } else {
        items.push({ key: 'causali', label: `Causali: ${selectedCausali.length}` });
      }
    }
    if (dateFrom) {
      items.push({ key: 'dateFrom', label: `Da: ${formatDisplayDate(dateFrom)}` });
    }
    if (dateTo) {
      items.push({ key: 'dateTo', label: `A: ${formatDisplayDate(dateTo)}` });
    }
    return items;
  }, [searchValue, azienda, aziendeOptions, selectedCausali, dateFrom, dateTo]);

  const clearActiveFilter = (key) => {
    if (key === 'search') {
      setSearchInput('');
      setSearchValue('');
      setPage(0);
      return;
    }
    if (key === 'azienda') {
      setAzienda('all');
      setPage(0);
      return;
    }
    if (key === 'causali') {
      setSelectedCausali([]);
      setPage(0);
      return;
    }
    if (key === 'dateFrom') {
      setDateFrom('');
      setPage(0);
      return;
    }
    if (key === 'dateTo') {
      setDateTo('');
      setPage(0);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#eef2ff',
              color: '#4f46e5',
            }}
          >
            <FileText size={22} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Prima Nota Passpartout
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Movimenti contabili sincronizzati dal gestionale Passpartout
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={handleSync}
          disabled={syncing}
          startIcon={
            syncing ? (
              <LoaderProgress size={20} intervalMs={80} step={15} maxBeforeDone={90} />
            ) : (
              <RefreshCw size={18} />
            )
          }
          sx={{
            textTransform: 'none',
            px: 2.5,
            py: 0.75,
          }}
        >
          {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
        </Button>
      </Box>

      <Dialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              mt: 1,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                bgcolor: 'rgba(34, 197, 94, 0.12)',
                color: '#16A34A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AlertTriangle size={22} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Sincronizza
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sei sicuro di volerlo fare?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Azienda</InputLabel>
              <Select
                value={syncAzienda}
                label="Azienda"
                onChange={(e) => setSyncAzienda(e.target.value)}
              >
                {aziendeOptions.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {syncAzienda ? (
              <>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={syncMode}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSyncMode(value);
                      if (value === 'incremental') {
                        setSyncRebuild(false);
                      }
                    }}
                  >
                    <FormControlLabel
                      value="incremental"
                      control={<Radio />}
                      label="Incrementale (da ultima data registrazione)"
                    />
                    <FormControlLabel
                      value="range"
                      control={<Radio />}
                      label="Intervallo date personalizzato"
                    />
                  </RadioGroup>
                </FormControl>

                {syncMode === 'incremental' ? (
                  <Alert severity="info" sx={{ alignItems: 'center' }}>
                    {loadingLatestDate ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <Typography variant="body2">
                          Recupero ultima data registrazione...
                        </Typography>
                      </Box>
                    ) : syncLatestDate ? (
                      `Verrà sincronizzato in modalità incrementale a partire dalla data ${formatDisplayDate(syncLatestDate)}.`
                    ) : (
                      'Nessuna data registrazione trovata per questa azienda: la sincronizzazione incrementale partirà da oggi.'
                    )}
                  </Alert>
                ) : (
                  <>
                    <TextField
                      label="Data inizio"
                      type="date"
                      size="small"
                      value={syncDateFrom}
                      onChange={(e) => setSyncDateFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="Data fine"
                      type="date"
                      size="small"
                      value={syncDateTo}
                      onChange={(e) => setSyncDateTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={syncRebuild}
                            onChange={(e) => setSyncRebuild(e.target.checked)}
                          />
                        }
                        label="Riallinea (cancella e risincronizza)"
                      />
                    </FormGroup>
                    {syncRebuild ? (
                      <Alert severity="warning">
                        Verranno cancellati i movimenti già importati nel periodo selezionato e poi
                        risincronizzati da Passpartout.
                      </Alert>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleConfirmSync} variant="contained" disabled={syncing}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Movimenti filtrati"
            value={summary.total || 0}
            icon={FileText}
            color="#2563EB"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Totale Dare"
            value={formatCurrency(summary.totalDare)}
            icon={ArrowDownCircle}
            color="#16A34A"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Totale Avere"
            value={formatCurrency(summary.totalAvere)}
            icon={ArrowUpCircle}
            color="#DC2626"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Saldo"
            value={saldoLabel}
            icon={TrendingUp}
            color="#CA8A04"
          />
        </Grid>
      </Grid>

      <StyledCard>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Cerca per conto, numero, descrizione..."
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260 }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Azienda</InputLabel>
              <Select
                value={azienda}
                label="Azienda"
                onChange={handleFilterAzienda}
              >
                <MenuItem value="all">Tutte</MenuItem>
                {aziendeOptions.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Mostra filtri avanzati">
              <IconButton size="small" onClick={handleToggleFilters}>
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Seleziona colonne aggiuntive">
              <IconButton size="small" onClick={handleOpenColumnMenu}>
                <ViewColumnIcon
                  fontSize="small"
                  color={columnVisibility.progressivo ? 'primary' : 'action'}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {activeFilters.length > 0 ? (
          <Box
            sx={{
              px: 3,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 0.5 }}>
              Filtri attivi
            </Typography>
            {activeFilters.map((filter) => (
              <Chip
                key={filter.key}
                label={filter.label}
                size="small"
                onDelete={() => clearActiveFilter(filter.key)}
                sx={(theme) => ({
                  fontWeight: 600,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.24)'
                      : '#F3F4F6',
                  color: theme.palette.text.primary,
                  border: '1px solid',
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.45)'
                      : 'rgba(148, 163, 184, 0.35)',
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiChip-deleteIcon:hover': {
                    color: theme.palette.text.primary,
                  },
                })}
              />
            ))}
          </Box>
        ) : null}

        <Menu
          anchorEl={columnMenuAnchorEl}
          open={Boolean(columnMenuAnchorEl)}
          onClose={handleCloseColumnMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Colonne aggiuntive
            </Typography>
          </Box>
          <FormGroup sx={{ px: 2, pb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.progressivo}
                  onChange={() => handleToggleColumn('progressivo')}
                />
              }
              label="Progressivo"
            />
          </FormGroup>
        </Menu>

        {showFilters && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Causale contabile</InputLabel>
              <Select
                multiple
                value={selectedCausali}
                label="Causale contabile"
                onChange={handleFilterCausali}
                renderValue={(selected) => selected.join(', ')}
              >
                {cauOptions.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Data da"
              type="date"
              size="small"
              value={dateFrom}
              onChange={handleFilterDateFrom}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon size={16} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Data a"
              type="date"
              size="small"
              value={dateTo}
              onChange={handleFilterDateTo}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon size={16} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'data_registr'}
                    direction={orderBy === 'data_registr' ? order : 'asc'}
                    onClick={() => handleRequestSort('data_registr')}
                  >
                    Data registrazione
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'azienda'}
                    direction={orderBy === 'azienda' ? order : 'asc'}
                    onClick={() => handleRequestSort('azienda')}
                  >
                    Azienda
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'cau_contabile'}
                    direction={orderBy === 'cau_contabile' ? order : 'asc'}
                    onClick={() => handleRequestSort('cau_contabile')}
                  >
                    Causale
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.progressivo && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'progressivo'}
                      direction={orderBy === 'progressivo' ? order : 'asc'}
                      onClick={() => handleRequestSort('progressivo')}
                    >
                      Progressivo
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>Numero</TableCell>
                <TableCell>Data documento</TableCell>
                <TableCell>Descrizione operazione</TableCell>
                <TableCell>Codice conto</TableCell>
                <TableCell>Denominazione</TableCell>
                <TableCell align="right">Dare</TableCell>
                <TableCell align="right">Avere</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    <LoaderProgress />
                  </TableCell>
                </TableRow>
              )}
              {empty && !loading && (
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    <Box
                      sx={{
                        py: 6,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Nessun movimento trovato
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Modifica i filtri o la ricerca per visualizzare i movimenti disponibili.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.data_registr
                        ? new Date(row.data_registr).toLocaleDateString('it-IT')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.azienda}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.cau_contabile || '-'}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      />
                    </TableCell>
                    {columnVisibility.progressivo && (
                      <TableCell>{row.progressivo || '-'}</TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.numero_formattato || row.nr_documento || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.testata_data_documento
                        ? new Date(row.testata_data_documento).toLocaleDateString('it-IT')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.descrizione_operazione || row.descr_riga || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.codice_conto || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.denominazione || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'success.main',
                        }}
                      >
                        {formatCurrency(row.dare)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'error.main',
                        }}
                      >
                        {formatCurrency(row.avere)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
